import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  FORMULARY_VALUE_SETS,
  type FormularyItemAdminView,
  type FormularyItemInput,
  type FormularyValueSet,
  type FrequencyView,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  addFormularyConcept,
  approveGroupMembership,
  createFormularyItem,
  formularyAdminQuery,
  formularyValueSetQuery,
  frequenciesQuery,
  replaceFrequencies,
  replaceGroups,
  updateFormularyItem,
} from '../api/prescribing.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../prescribing/prescribing.module.css';
import { StockItemPicker } from '../stock/item-picker.tsx';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Formulary (docs/plans/phase1-consultation.md §6.2, §7.5). The pharmacist in
// charge keeps the items clinicians prescribe: ingredients and strengths, forms, routes,
// the stock product, and the lists behind them. Group memberships are clinical knowledge:
// each has a source and needs a second person's approval. Frequencies, routes and forms
// start as Wodi's proposal, labelled "Check before go-live" until someone confirms them.

const LIST_LABELS: Record<FormularyValueSet, string> = {
  dose_form: 'Dose forms',
  medication_route: 'Routes',
  drug_substance: 'Substances',
  allergen_group: 'Allergen groups',
  therapeutic_group: 'Therapeutic groups',
};

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function itemName(item: FormularyItemAdminView): string {
  return `${item.genericName} ${item.strengthDisplay} ${item.doseForm.display.toLowerCase()}`;
}

interface IngredientDraft {
  substanceCode: string;
  numerator: string;
  numeratorUnit: string;
  denominator: string;
  denominatorUnit: string;
}

interface ItemDraft {
  genericName: string;
  brandName: string;
  doseFormCode: string;
  strengthDisplay: string;
  doseUnitCode: string;
  doseUnitDisplay: string;
  serviceItem: { id: string; name: string } | null;
  contentValue: string;
  contentUnit: string;
  quantityCalculable: boolean;
  routeCodes: string[];
  defaultRouteCode: string;
  isControlled: boolean;
  ingredients: IngredientDraft[];
}

function draftOf(item: FormularyItemAdminView | null): ItemDraft {
  if (item === null) {
    return {
      genericName: '',
      brandName: '',
      doseFormCode: '',
      strengthDisplay: '',
      doseUnitCode: '',
      doseUnitDisplay: '',
      serviceItem: null,
      contentValue: '',
      contentUnit: '',
      quantityCalculable: true,
      routeCodes: [],
      defaultRouteCode: '',
      isControlled: false,
      ingredients: [
        {
          substanceCode: '',
          numerator: '',
          numeratorUnit: 'mg',
          denominator: '1',
          denominatorUnit: '',
        },
      ],
    };
  }
  return {
    genericName: item.genericName,
    brandName: item.brandName ?? '',
    doseFormCode: item.doseForm.code,
    strengthDisplay: item.strengthDisplay,
    doseUnitCode: item.doseUnit.code,
    doseUnitDisplay: item.doseUnit.display,
    serviceItem:
      item.serviceItemId === null
        ? null
        : { id: item.serviceItemId, name: item.serviceItemName ?? '' },
    contentValue: item.baseUnitContent?.value ?? '',
    contentUnit: item.baseUnitContent?.unit ?? '',
    quantityCalculable: item.quantityCalculable,
    routeCodes: item.routes.map((r) => r.code),
    defaultRouteCode: item.defaultRouteCode ?? '',
    isControlled: item.isControlled,
    ingredients: item.ingredients.map((i) => ({
      substanceCode: i.code,
      numerator: i.strengthNumerator.value,
      numeratorUnit: i.strengthNumerator.unit,
      denominator: i.strengthDenominator.value,
      denominatorUnit: i.strengthDenominator.unit,
    })),
  };
}

function inputOf(d: ItemDraft): FormularyItemInput {
  return {
    genericName: d.genericName.trim(),
    brandName: d.brandName.trim() === '' ? null : d.brandName.trim(),
    doseFormCode: d.doseFormCode,
    strengthDisplay: d.strengthDisplay.trim(),
    doseUnit: {
      code: d.doseUnitCode.trim(),
      display: (d.doseUnitDisplay.trim() || d.doseUnitCode).trim(),
    },
    serviceItemId: d.serviceItem?.id ?? null,
    baseUnitContent:
      d.contentValue.trim() === ''
        ? null
        : { value: d.contentValue.trim(), unit: d.contentUnit.trim() },
    quantityCalculable: d.quantityCalculable,
    routeCodes: d.routeCodes,
    defaultRouteCode: d.defaultRouteCode === '' ? null : d.defaultRouteCode,
    isControlled: d.isControlled,
    ingredients: d.ingredients.map((i) => ({
      substanceCode: i.substanceCode,
      strengthNumerator: { value: i.numerator.trim(), unit: i.numeratorUnit.trim() },
      strengthDenominator: {
        value: i.denominator.trim(),
        unit: (i.denominatorUnit.trim() || d.doseUnitCode).trim(),
      },
    })),
  };
}

function ItemDialog({
  item,
  onClose,
}: {
  item: FormularyItemAdminView | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const forms = useQuery(formularyValueSetQuery('dose_form'));
  const routes = useQuery(formularyValueSetQuery('medication_route'));
  const substances = useQuery(formularyValueSetQuery('drug_substance'));
  const [d, setD] = useState<ItemDraft>(() => draftOf(item));
  const set = (patch: Partial<ItemDraft>) => {
    setD((current) => ({ ...current, ...patch }));
  };
  const save = useMutation({
    mutationFn: () =>
      item === null
        ? createFormularyItem(inputOf(d))
        : updateFormularyItem(item.id, { ...inputOf(d), expectedVersion: item.version }),
    onSuccess: () => {
      toasts.show({ title: item === null ? 'Formulary item added' : 'Formulary item saved' });
      void queryClient.invalidateQueries({ queryKey: formularyAdminQuery.queryKey });
      onClose();
    },
  });
  const active = <T extends { status: string }>(list: readonly T[] | undefined) =>
    (list ?? []).filter((c) => c.status === 'active');
  return (
    <Dialog
      title={item === null ? 'Add formulary item' : `Edit ${itemName(item)}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.panel}>
        <div className={styles.fields}>
          <TextField
            label="Generic name"
            value={d.genericName}
            onChange={(genericName) => {
              set({ genericName });
            }}
          />
          <TextField
            label="Brand name"
            description="Optional."
            value={d.brandName}
            onChange={(brandName) => {
              set({ brandName });
            }}
          />
          <Select
            label="Dose form"
            value={d.doseFormCode === '' ? null : d.doseFormCode}
            onChange={(key) => {
              set({ doseFormCode: String(key) });
            }}
            options={active(forms.data).map((c) => ({ id: c.code, label: c.display }))}
          />
          <TextField
            label="Strength as printed"
            description="For example 250 mg/5 mL."
            value={d.strengthDisplay}
            onChange={(strengthDisplay) => {
              set({ strengthDisplay });
            }}
          />
        </div>
        <div className={styles.fields}>
          <TextField
            label="Dose unit code"
            description="What a dose is counted in: tablet, capsule, mL."
            value={d.doseUnitCode}
            onChange={(doseUnitCode) => {
              set({ doseUnitCode });
            }}
          />
          <TextField
            label="Dose unit as shown"
            value={d.doseUnitDisplay}
            onChange={(doseUnitDisplay) => {
              set({ doseUnitDisplay });
            }}
          />
          <TextField
            label="Content per stock unit"
            description="For liquids: mL in one bottle. Leave empty for tablets."
            value={d.contentValue}
            onChange={(contentValue) => {
              set({ contentValue });
            }}
          />
          <TextField
            label="Content unit"
            value={d.contentUnit}
            onChange={(contentUnit) => {
              set({ contentUnit });
            }}
          />
        </div>
        <fieldset className={styles.list}>
          <legend>Ingredients and strength</legend>
          {d.ingredients.map((ing, index) => {
            const update = (patch: Partial<IngredientDraft>) => {
              set({
                ingredients: d.ingredients.map((x, i) => (i === index ? { ...x, ...patch } : x)),
              });
            };
            return (
              <div key={index} className={styles.fields}>
                <Select
                  label="Substance"
                  value={ing.substanceCode === '' ? null : ing.substanceCode}
                  onChange={(key) => {
                    update({ substanceCode: String(key) });
                  }}
                  options={active(substances.data).map((c) => ({ id: c.code, label: c.display }))}
                />
                <TextField
                  label="Amount"
                  inputMode="decimal"
                  value={ing.numerator}
                  onChange={(numerator) => {
                    update({ numerator });
                  }}
                />
                <TextField
                  label="Amount unit"
                  description="mg, ug, g, mL, [iU], mmol"
                  value={ing.numeratorUnit}
                  onChange={(numeratorUnit) => {
                    update({ numeratorUnit });
                  }}
                />
                <TextField
                  label="Per"
                  inputMode="decimal"
                  value={ing.denominator}
                  onChange={(denominator) => {
                    update({ denominator });
                  }}
                />
                <TextField
                  label="Per unit"
                  description="Defaults to the dose unit."
                  value={ing.denominatorUnit}
                  onChange={(denominatorUnit) => {
                    update({ denominatorUnit });
                  }}
                />
                {d.ingredients.length > 1 ? (
                  <Button
                    variant="quiet"
                    onPress={() => {
                      set({ ingredients: d.ingredients.filter((_, i) => i !== index) });
                    }}
                  >
                    Remove ingredient
                  </Button>
                ) : null}
              </div>
            );
          })}
          <Button
            variant="quiet"
            onPress={() => {
              set({
                ingredients: [
                  ...d.ingredients,
                  {
                    substanceCode: '',
                    numerator: '',
                    numeratorUnit: 'mg',
                    denominator: '1',
                    denominatorUnit: '',
                  },
                ],
              });
            }}
          >
            Add ingredient
          </Button>
        </fieldset>
        <fieldset className={styles.fields}>
          <legend>Routes</legend>
          {active(routes.data).map((r) => (
            <Checkbox
              key={r.code}
              isSelected={d.routeCodes.includes(r.code)}
              onChange={(on) => {
                set({
                  routeCodes: on
                    ? [...d.routeCodes, r.code]
                    : d.routeCodes.filter((c) => c !== r.code),
                });
              }}
            >
              {r.display}
            </Checkbox>
          ))}
        </fieldset>
        <div className={styles.fields}>
          <Select
            label="Default route"
            value={d.defaultRouteCode === '' ? null : d.defaultRouteCode}
            onChange={(key) => {
              set({ defaultRouteCode: String(key) });
            }}
            options={active(routes.data)
              .filter((r) => d.routeCodes.includes(r.code))
              .map((r) => ({ id: r.code, label: r.display }))}
          />
          <Checkbox
            isSelected={d.quantityCalculable}
            onChange={(quantityCalculable) => {
              set({ quantityCalculable });
            }}
          >
            Work out the quantity from the course
          </Checkbox>
          <Checkbox
            isSelected={d.isControlled}
            onChange={(isControlled) => {
              set({ isControlled });
            }}
          >
            Controlled drug
          </Checkbox>
        </div>
        <div>
          <p>
            {d.serviceItem === null
              ? 'Not linked to a stock product: prescribed as "Buy outside".'
              : `Stock product: ${d.serviceItem.name}`}
          </p>
          <StockItemPicker
            label="Stock product"
            onSelect={(s) => {
              set({ serviceItem: { id: s.itemId, name: s.name } });
            }}
          />
          {d.serviceItem === null ? null : (
            <Button
              variant="quiet"
              onPress={() => {
                set({ serviceItem: null });
              }}
            >
              Unlink stock product
            </Button>
          )}
        </div>
        {save.isError ? (
          <Notice tone="error">
            {message(save.error, 'The item was not saved. Check the fields and try again.')}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button onPress={onClose}>Cancel</Button>
        <Button
          variant="primary"
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            save.mutate();
          }}
        >
          {item === null ? 'Add item' : 'Save item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GroupsDialog({
  item,
  mayApprove,
  userId,
  onClose,
}: {
  item: FormularyItemAdminView;
  mayApprove: boolean;
  userId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const allergen = useQuery(formularyValueSetQuery('allergen_group'));
  const therapeutic = useQuery(formularyValueSetQuery('therapeutic_group'));
  const subjects = [
    ...item.ingredients.map((i) => ({
      id: `substance:${i.code}`,
      label: `Substance: ${i.display}`,
      type: 'substance' as const,
      subject: i.code,
    })),
    {
      id: `formulary_item:${item.id}`,
      label: `This product: ${itemName(item)}`,
      type: 'formulary_item' as const,
      subject: item.id,
    },
  ];
  const [subjectId, setSubjectId] = useState(subjects[0]?.id ?? '');
  const [kind, setKind] = useState<'allergen_group' | 'therapeutic_group'>('allergen_group');
  const [group, setGroup] = useState('');
  const [source, setSource] = useState('');
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: formularyAdminQuery.queryKey });
  const add = useMutation({
    mutationFn: () => {
      const subject = subjects.find((s) => s.id === subjectId);
      if (subject === undefined) throw new Error('no subject');
      const current = item.groups
        .filter((g) => g.subjectType === subject.type && g.subject === subject.subject)
        .map((g) => ({ groupKind: g.groupKind, groupCode: g.group.code, source: g.source }));
      return replaceGroups({
        subjectType: subject.type,
        subject: subject.subject,
        groups: [...current, { groupKind: kind, groupCode: group, source: source.trim() }],
      });
    },
    onSuccess: () => {
      toasts.show({ title: 'Group added as provisional' });
      setGroup('');
      setSource('');
      refresh();
    },
  });
  const approve = useMutation({
    mutationFn: (g: { id: string; version: number }) => approveGroupMembership(g.id, g.version),
    onSuccess: () => {
      toasts.show({ title: 'Group membership approved' });
      refresh();
    },
  });
  const options = (kind === 'allergen_group' ? allergen.data : therapeutic.data) ?? [];
  return (
    <Dialog
      title={`Groups: ${itemName(item)}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.panel}>
        {item.groups.length === 0 ? (
          <p className={styles.empty}>
            No groups yet. Allergy and duplicate checks only match this drug by its own substances
            until groups are added.
          </p>
        ) : (
          <ul className={styles.list}>
            {item.groups.map((g) => (
              <li key={g.id} className={styles.rxLine}>
                <Tag state={g.approval === 'approved' ? 'done' : 'pending'}>
                  {g.approval === 'approved' ? 'Approved' : 'Provisional'}
                </Tag>
                <span className={styles.sig}>
                  {`${g.groupKind === 'allergen_group' ? 'Allergen group' : 'Therapeutic group'}: ${g.group.display}. Source: ${g.source}`}
                </span>
                {mayApprove && g.approval === 'provisional' && g.changedBy !== userId ? (
                  <Button
                    dense
                    onPress={() => {
                      approve.mutate({ id: g.id, version: g.version });
                    }}
                  >
                    Approve
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <div className={styles.fields}>
          <Select
            label="For"
            value={subjectId}
            onChange={(k) => {
              setSubjectId(String(k));
            }}
            options={subjects.map((s) => ({ id: s.id, label: s.label }))}
          />
          <Select
            label="Kind"
            value={kind}
            onChange={(k) => {
              setKind(k === 'therapeutic_group' ? 'therapeutic_group' : 'allergen_group');
              setGroup('');
            }}
            options={[
              { id: 'allergen_group', label: 'Allergen group' },
              { id: 'therapeutic_group', label: 'Therapeutic group' },
            ]}
          />
          <Select
            label="Group"
            value={group === '' ? null : group}
            onChange={(k) => {
              setGroup(String(k));
            }}
            options={options
              .filter((c) => c.status === 'active')
              .map((c) => ({ id: c.code, label: c.display }))}
          />
          <TextField
            label="Source"
            description="Guideline, edition and page."
            value={source}
            onChange={setSource}
          />
        </div>
        {add.isError ? (
          <Notice tone="error">{message(add.error, 'The group was not added. Try again.')}</Notice>
        ) : null}
        {approve.isError ? (
          <Notice tone="error">
            {message(approve.error, 'That was not approved. Try again.')}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button onPress={onClose}>Close</Button>
        <Button
          variant="primary"
          isDisabled={group === '' || source.trim().length < 3}
          isPending={add.isPending}
          pendingLabel="Adding…"
          onPress={() => {
            add.mutate();
          }}
        >
          Add group
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ListEditor({ valueSet }: { valueSet: FormularyValueSet }) {
  const queryClient = useQueryClient();
  const list = useQuery(formularyValueSetQuery(valueSet));
  const [display, setDisplay] = useState('');
  const add = useMutation({
    mutationFn: () => addFormularyConcept(valueSet, display.trim()),
    onSuccess: () => {
      setDisplay('');
      void queryClient.invalidateQueries({ queryKey: formularyValueSetQuery(valueSet).queryKey });
    },
  });
  return (
    <section aria-label={LIST_LABELS[valueSet]} className={styles.rx}>
      <h3 className={styles.lineTitle}>{LIST_LABELS[valueSet]}</h3>
      {list.isPending ? <LoadingBlock label={`Loading ${LIST_LABELS[valueSet]}`} rows={1} /> : null}
      <p>
        {(list.data ?? []).length === 0
          ? 'None yet.'
          : (list.data ?? [])
              .map((c) => `${c.display}${c.provisional ? ' (check before go-live)' : ''}`)
              .join(', ')}
      </p>
      <div className={styles.fields}>
        <TextField
          label={`Add to ${LIST_LABELS[valueSet].toLowerCase()}`}
          value={display}
          onChange={setDisplay}
        />
        <Button
          isDisabled={display.trim() === ''}
          isPending={add.isPending}
          pendingLabel="Adding…"
          onPress={() => {
            add.mutate();
          }}
        >
          Add
        </Button>
      </div>
      {add.isError ? (
        <Notice tone="error">{message(add.error, 'That was not added. Try again.')}</Notice>
      ) : null}
    </section>
  );
}

function Frequencies() {
  const queryClient = useQueryClient();
  const list = useQuery(frequenciesQuery);
  const confirm = useMutation({
    mutationFn: (items: readonly FrequencyView[]) =>
      replaceFrequencies({
        items: items.map((f) => ({
          code: f.code,
          display: f.display,
          abbreviation: f.abbreviation,
          kind: f.kind,
          dosesPerDay: f.dosesPerDayScaled === null ? null : String(f.dosesPerDayScaled / 1000),
          intervalHours: f.intervalHours,
          confirmed: true,
          status: f.status,
        })),
      }),
    onSuccess: () => {
      toasts.show({ title: 'Frequencies confirmed' });
      void queryClient.invalidateQueries({ queryKey: frequenciesQuery.queryKey });
    },
  });
  if (list.isPending) return <LoadingBlock label="Loading frequencies" />;
  if (list.isError)
    return <Notice tone="error">{message(list.error, 'Frequencies could not load.')}</Notice>;
  const provisional = list.data.items.some((f) => f.provisional);
  return (
    <section aria-label="Frequencies" className={styles.list}>
      <h2>Frequencies</h2>
      {provisional ? (
        <Notice tone="info">
          These frequencies are Wodi’s proposal. Check before go-live: the pharmacist in charge
          confirms them.
        </Notice>
      ) : null}
      <Table aria-label="Frequencies" dense>
        <TableHeader>
          <Column isRowHeader>How often</Column>
          <Column>Short form</Column>
          <Column numeric>Doses a day</Column>
          <Column>State</Column>
        </TableHeader>
        <TableBody items={list.data.items}>
          {(f) => (
            <Row id={f.code}>
              <Cell>{f.display}</Cell>
              <Cell>{f.abbreviation ?? ''}</Cell>
              <Cell>
                {f.kind === 'regular' && f.dosesPerDayScaled !== null
                  ? String(f.dosesPerDayScaled / 1000)
                  : f.kind === 'once'
                    ? 'Once'
                    : 'When needed'}
              </Cell>
              <Cell>
                {f.provisional
                  ? 'Check before go-live'
                  : f.status === 'active'
                    ? 'Confirmed'
                    : 'Off'}
              </Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      {provisional ? (
        <Button
          isPending={confirm.isPending}
          pendingLabel="Confirming…"
          onPress={() => {
            confirm.mutate(list.data.items);
          }}
        >
          Confirm these frequencies
        </Button>
      ) : null}
      {confirm.isError ? (
        <Notice tone="error">
          {message(confirm.error, 'The frequencies were not confirmed.')}
        </Notice>
      ) : null}
    </section>
  );
}

export function FormularySettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const items = useQuery({
    ...formularyAdminQuery,
    enabled: session !== null && can(session, 'formulary.manage'),
  });
  const [editing, setEditing] = useState<FormularyItemAdminView | null | 'new'>(null);
  const [grouping, setGrouping] = useState<FormularyItemAdminView | null>(null);

  if (session === null || !can(session, 'formulary.manage')) {
    return (
      <>
        <PageHeader title="Formulary" />
        <Notice tone="info">
          You don't have permission to change the formulary. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  const mayApprove = can(session, 'clinical_rules.approve');
  return (
    <SettingsPage
      title="Formulary"
      actions={
        <Button
          variant="primary"
          isDisabled={!online}
          onPress={() => {
            setEditing('new');
          }}
        >
          Add item
        </Button>
      }
    >
      {!online ? (
        <Notice tone="info">Changing the formulary needs a connection to Wodi.</Notice>
      ) : null}
      {items.isPending ? <LoadingBlock label="Loading the formulary" rows={6} /> : null}
      {items.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void items.refetch()}>
              Try again
            </Button>
          }
        >
          {message(items.error, 'The formulary could not load. Try again.')}
        </Notice>
      ) : null}
      {items.data === undefined ? null : (
        <Table aria-label="Formulary items">
          <TableHeader>
            <Column isRowHeader>Item</Column>
            <Column>Ingredients</Column>
            <Column>Routes</Column>
            <Column>Stock product</Column>
            <Column>Groups</Column>
            <Column>Actions</Column>
          </TableHeader>
          <TableBody
            items={items.data.items}
            renderEmptyState={() => 'No items yet. Add the drugs clinicians prescribe here.'}
          >
            {(item) => (
              <Row id={item.id}>
                <Cell>
                  {itemName(item)}
                  {item.brandName === null ? '' : ` (${item.brandName})`}
                  {item.status === 'inactive' ? ' — off' : ''}
                </Cell>
                <Cell>
                  {item.ingredients
                    .map(
                      (i) =>
                        `${i.display} ${i.strengthNumerator.value} ${i.strengthNumerator.unit}/${i.strengthDenominator.value} ${i.strengthDenominator.unit}`,
                    )
                    .join('; ')}
                </Cell>
                <Cell>{item.routes.map((r) => r.display).join(', ')}</Cell>
                <Cell>{item.serviceItemName ?? 'Buy outside'}</Cell>
                <Cell>
                  {item.groups.length === 0
                    ? 'None'
                    : item.groups
                        .map(
                          (g) =>
                            `${g.group.display}${g.approval === 'provisional' ? ' (provisional)' : ''}`,
                        )
                        .join(', ')}
                </Cell>
                <Cell>
                  <Button
                    dense
                    isDisabled={!online}
                    onPress={() => {
                      setEditing(item);
                    }}
                  >
                    Edit
                  </Button>{' '}
                  <Button
                    dense
                    isDisabled={!online}
                    onPress={() => {
                      setGrouping(item);
                    }}
                  >
                    Groups
                  </Button>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      )}
      <h2>Lists</h2>
      {FORMULARY_VALUE_SETS.map((set) => (
        <ListEditor key={set} valueSet={set} />
      ))}
      <Frequencies />
      {editing === null ? null : (
        <ItemDialog
          item={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
      {grouping === null ? null : (
        <GroupsDialog
          item={items.data?.items.find((i) => i.id === grouping.id) ?? grouping}
          mayApprove={mayApprove}
          userId={session.user.id}
          onClose={() => {
            setGrouping(null);
          }}
        />
      )}
    </SettingsPage>
  );
}
